import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@edusense/shared-auth-core';
import { Loader2 } from 'lucide-react';
import { getAdminMfaStatus } from '../auth/adminMfaApi';
import { adminAuthConfig } from '../auth/adminAuthConfig';
import {
  buildPendingMfaRedirectWithChallenge,
  shouldCheckAdminMfaGuard,
  shouldStartInteractiveAdminLogin,
} from '../auth/adminMfaRouting';
import { restartAdminLogin } from '../auth/restartAdminLogin';
import { shouldOfferAdminLoginRestart } from '../types/adminMfa';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

export interface AuthGuardProps {
  children: React.ReactNode;
  allowPendingMfaAccess?: boolean;
}

type MfaGuardState = {
  checkedFor: string | null;
  error: string | null;
};

const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401) return '관리자 세션이 만료되었습니다. 다시 로그인해 주세요.';
    if (error.response?.status === 403) return '이 계정에는 관리자 접근 권한이 없습니다.';
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const message = record['message'] || record['error'] || record['detail'];
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return '관리자 MFA 상태를 확인하지 못했습니다.';
};
/**
 * 전역 인증 가드 컴포넌트
 * 미인증 상태일 경우 자동으로 OIDC 로그인 플로우를 시작합니다.
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ children, allowPendingMfaAccess = false }) => {
  const { isAuthenticated, isLoading, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mfaState, setMfaState] = useState<MfaGuardState>({
    checkedFor: null,
    error: null,
  });
  const mfaCheckKey = JSON.stringify([
    token, isAuthenticated, isLoading, allowPendingMfaAccess,
    location.pathname, location.search, location.hash,
  ]);
  const authConfig = adminAuthConfig;
  const authServerUrl = authConfig.api.authServerUrl || import.meta.env.VITE_AUTH_SERVER_URL || 'https://auth.ugot.uk';

  useEffect(() => {
    // 로딩도 끝났고 인증도 안 되어 있으면 랜딩 페이지로 보냄
    if (shouldStartInteractiveAdminLogin({ isLoading, isAuthenticated, allowPendingMfaAccess })) {
      navigate('/landing', { replace: true });
    }
  }, [allowPendingMfaAccess, isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (!shouldCheckAdminMfaGuard({ isLoading, isAuthenticated, allowPendingMfaAccess })) {
      setMfaState({ checkedFor: null, error: null });
      return;
    }

    const controller = new AbortController();

    const checkMfaStatus = async () => {
      setMfaState({ checkedFor: null, error: null });

      try {
        const status = await getAdminMfaStatus(controller.signal);
        if (controller.signal.aborted) return;
        if (!allowPendingMfaAccess && !status.sessionMfaVerified) {
          navigate(buildPendingMfaRedirectWithChallenge(
            location.pathname,
            location.search,
            location.hash,
            status.challengeId,
          ), {
            replace: true,
            state: {
              from: `${location.pathname}${location.search}${location.hash}`,
            },
          });
          return;
        }

        setMfaState({ checkedFor: mfaCheckKey, error: null });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setMfaState({ checkedFor: mfaCheckKey, error: getErrorMessage(error) });
      }
    };

    void checkMfaStatus();
    return () => controller.abort();
  }, [allowPendingMfaAccess, isAuthenticated, isLoading, mfaCheckKey, location.hash, location.pathname, location.search, navigate]);

  // 로딩 중일 때는 빈 화면이나 스피너를 보여줍니다.
  if (isLoading || (shouldCheckAdminMfaGuard({ isLoading, isAuthenticated, allowPendingMfaAccess }) && mfaState.checkedFor !== mfaCheckKey)) {
    return (
      <div className="flex flex-col h-screen w-full items-center justify-center bg-gray-50 space-y-4">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
        <span className="text-gray-500 font-medium">
          {isAuthenticated ? '관리자 MFA 세션을 확인하는 중입니다...' : '인증 정보를 확인하는 중입니다...'}
        </span>
      </div>
    );
  }

  // 인증이 확인된 경우에만 자식 컴포넌트(대시보드 등)를 렌더링합니다.
  if (isAuthenticated || allowPendingMfaAccess) {
    if (mfaState.checkedFor === mfaCheckKey && mfaState.error) {
      const isAuthError = mfaState.error.includes('권한') || mfaState.error.includes('만료');
      
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
          <Card className="w-full max-w-lg">
            <CardContent className="space-y-4 py-6">
              <div className="space-y-1">
                <p className="text-base font-semibold text-red-600">
                  {isAuthError ? '인증 실패' : '관리자 MFA 상태 확인 실패'}
                </p>
                <p className="text-sm text-muted-foreground">{mfaState.error}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                {isAuthError ? (
                  <Button
                    type="button"
                    onClick={() => {
                      void restartAdminLogin(navigate, authConfig, authServerUrl);
                    }}
                  >
                    관리자 계정으로 다시 로그인
                  </Button>
                ) : (
                  <>
                    <Button type="button" onClick={() => window.location.reload()}>
                      다시 시도
                    </Button>
                    {shouldOfferAdminLoginRestart(mfaState.error) && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          void restartAdminLogin(navigate, authConfig, authServerUrl);
                        }}
                      >
                        로그인 다시 시작
                      </Button>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return <>{children}</>;
  }

  // startOAuthLogin이 호출되어 리다이렉트 중일 때는 껍데기조차 노출시키지 않습니다.
  return (
    <div className="flex h-screen w-full items-center justify-center bg-gray-50">
      <span className="text-gray-500">로그인 페이지로 이동 중입니다...</span>
    </div>
  );
};

export default AuthGuard;
